"use client"

import { MapPin } from "lucide-react"
import Link from "next/link"
import { SOD_GRASSES, type SodGrassId } from "@/lib/sod/calculator"
import { ZIP_INPUT_ATTRS, sanitizeZip } from "@/lib/zip-input"

interface SodFormProps {
  area: number
  grassId: SodGrassId
  zip: string
  regionLabel: string
  regionKnown: boolean
  onAreaChange: (area: number) => void
  onGrassChange: (grassId: SodGrassId) => void
  onZipChange: (zip: string) => void
}

const SIZE_PRESETS = [500, 2000, 5000, 10000]

// Log-scale slider bounds: 10^2 = 100 sq ft .. 10^4.48 ~= 30,000 sq ft.
const SLIDER_MIN = 2
const SLIDER_MAX = 4.48

const stepLabel: React.CSSProperties = {
  margin: "0 0 12px",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#15803d",
}

const optionalLabel: React.CSSProperties = {
  fontWeight: 600,
  textTransform: "none",
  letterSpacing: 0,
  color: "#6b7280",
}

export function SodForm({
  area,
  grassId,
  zip,
  regionLabel,
  regionKnown,
  onAreaChange,
  onGrassChange,
  onZipChange,
}: SodFormProps) {
  const areaLog = Math.log10(Math.max(100, area || 100))

  return (
    <div
      style={{
        borderRadius: 24,
        background: "#fff",
        border: "1px solid #e5e7eb",
        padding: 28,
        boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
      }}
    >
      {/* 1 · Area */}
      <p style={stepLabel}>1 · Area to sod</p>
      <div style={{ position: "relative" }}>
        <label htmlFor="sod-area" className="sr-only">
          Area to sod in square feet
        </label>
        <input
          id="sod-area"
          type="number"
          inputMode="numeric"
          min={100}
          value={area || ""}
          onChange={(e) => {
            const v = parseFloat(e.target.value)
            onAreaChange(Number.isNaN(v) ? 0 : Math.max(0, v))
          }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            minHeight: 52,
            padding: "0 74px 0 16px",
            fontFamily: "inherit",
            fontSize: 17,
            fontWeight: 700,
            color: "#111827",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "#fff",
            outline: "none",
          }}
        />
        <span
          style={{
            position: "absolute",
            top: "50%",
            right: 16,
            transform: "translateY(-50%)",
            fontSize: 13,
            fontWeight: 600,
            color: "#6b7280",
          }}
        >
          sq ft
        </span>
      </div>
      {area < 100 && (
        <p role="alert" style={{ margin: "8px 0 0", fontSize: 13, fontWeight: 600, color: "#b45309" }}>
          Enter at least 100 sq ft for a useful sod estimate.
        </p>
      )}
      <label htmlFor="sod-area-slider" className="sr-only">
        Adjust area with slider
      </label>
      <input
        id="sod-area-slider"
        type="range"
        min={SLIDER_MIN}
        max={SLIDER_MAX}
        step={0.01}
        value={areaLog}
        onChange={(e) =>
          onAreaChange(Math.round(Math.pow(10, parseFloat(e.target.value)) / 50) * 50)
        }
        style={{ width: "100%", marginTop: 12, accentColor: "#16a34a" }}
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        {SIZE_PRESETS.map((v) => {
          const selected = v === area
          return (
            <button
              key={v}
              type="button"
              onClick={() => onAreaChange(v)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                boxSizing: "border-box",
                minHeight: 36,
                padding: "0 14px",
                border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                borderRadius: 9999,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 700,
                color: "#111827",
                background: selected ? "#f0fdf4" : "#fff",
              }}
            >
              {v.toLocaleString()} sq ft
            </button>
          )
        })}
      </div>
      <p style={{ margin: "10px 0 0", fontSize: 13, color: "#6b7280" }}>
        Sodding a section, not the whole lawn? Measure just that area with the{" "}
        <Link
          href="/tools/lawn-size-calculator"
          style={{
            fontWeight: 600,
            color: "#16a34a",
            textDecoration: "underline",
            textUnderlineOffset: 2,
          }}
        >
          lawn size calculator
        </Link>
        .
      </p>

      {/* 2 · Sod type */}
      <p style={{ ...stepLabel, margin: "24px 0 12px" }}>
        2 · Sod type <span style={optionalLabel}>(optional)</span>
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} role="group" aria-label="Sod type">
        {SOD_GRASSES.map((g) => {
          const selected = g.id === grassId
          return (
            <button
              key={g.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onGrassChange(g.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                boxSizing: "border-box",
                minHeight: 40,
                padding: "0 16px",
                border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                borderRadius: 9999,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: 700,
                color: selected ? "#166534" : "#374151",
                background: selected ? "#f0fdf4" : "#fff",
              }}
            >
              {g.label}
            </button>
          )
        })}
      </div>

      {/* 3 · ZIP */}
      <p style={{ ...stepLabel, margin: "24px 0 10px" }}>
        3 · Your ZIP code <span style={optionalLabel}>(optional, for local pricing)</span>
      </p>
      <div style={{ position: "relative" }}>
        <MapPin
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "50%",
            left: 16,
            transform: "translateY(-50%)",
            width: 18,
            height: 18,
            color: "#9ca3af",
          }}
        />
        <label htmlFor="sod-zip" className="sr-only">
          ZIP code for local pricing
        </label>
        <input
          id="sod-zip"
          {...ZIP_INPUT_ATTRS}
          value={zip}
          placeholder="e.g. 30301"
          onChange={(e) => onZipChange(sanitizeZip(e.target.value))}
          style={{
            width: "100%",
            boxSizing: "border-box",
            minHeight: 52,
            padding: "0 130px 0 44px",
            fontFamily: "inherit",
            fontSize: 17,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "#111827",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "#fff",
            outline: "none",
          }}
        />
        <span
          style={{
            position: "absolute",
            top: "50%",
            right: 14,
            transform: "translateY(-50%)",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 12px",
            borderRadius: 9999,
            fontSize: 12,
            fontWeight: 700,
            color: regionKnown ? "#166534" : "#6b7280",
            background: regionKnown ? "#f0fdf4" : "#f9fafb",
            border: `1px solid ${regionKnown ? "#bbf7d0" : "#e5e7eb"}`,
          }}
        >
          {regionLabel}
        </span>
      </div>
    </div>
  )
}
